"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { type Direction, translate } from "@/lib/tool-kit/text/pig-latin";

/** English strings. A translated content file overrides these through `ui`. */
export const PIG_LATIN_TRANSLATOR_STRINGS = {
  direction: "Direction",
  englishToPigLatin: "English → Pig Latin",
  pigLatinToEnglish: "Pig Latin → English",
  reverseHeuristicNote:
    'Reverse translation is a heuristic — Pig Latin is ambiguous in reverse, so some words (like "itchway") have more than one valid English original.',
  englishText: "English text",
  pigLatinText: "Pig Latin text",
  englishPlaceholder: "Hello, world!",
  pigLatinPlaceholder: "Ellohay, orldway!",
  pigLatinLabel: "Pig Latin",
  englishBestEffort: "English (best effort)",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  rulesHeading: "The Rules of Pig Latin",
  ruleLabels: {
    startsWithVowel: 'Starts with a vowel → add "way"',
    startsWithConsonant: 'Starts with a consonant → move it, add "ay"',
    consonantCluster: "Consonant cluster moves as a whole",
    quUnit: '"qu" travels as one unit',
    capitalizationPreserved: "Capitalization and punctuation are preserved",
  },
  ruleExamples: {
    startsWithVowel: "apple → appleway · orange → orangeway",
    startsWithConsonant: "hello → ellohay · pig → igpay",
    consonantCluster: "string → ingstray · three → eethray",
    quUnit: "question → estionquay · squash → ashsquay",
    capitalizationPreserved: "Hello, world! → Ellohay, orldway!",
  },
} as const;

const RULE_IDS = [
  "startsWithVowel",
  "startsWithConsonant",
  "consonantCluster",
  "quUnit",
  "capitalizationPreserved",
] as const;

export default function PigLatinTranslatorWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("to-pig");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(PIG_LATIN_TRANSLATOR_STRINGS);

  const DIRECTIONS: { value: Direction; label: string }[] = [
    { value: "to-pig", label: strings.englishToPigLatin },
    { value: "to-english", label: strings.pigLatinToEnglish },
  ];

  const output = input.trim().length === 0 ? "" : translate(input, direction);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {DIRECTIONS.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {d.label}
            </label>
          ))}
        </div>
        {direction === "to-english" && (
          <p className="text-muted-foreground text-xs">
            {strings.reverseHeuristicNote}
          </p>
        )}
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="pig-input"
        >
          {direction === "to-pig" ? strings.englishText : strings.pigLatinText}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="pig-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={
            direction === "to-pig"
              ? strings.englishPlaceholder
              : strings.pigLatinPlaceholder
          }
          value={input}
        />
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="pig-output"
          >
            {direction === "to-pig"
              ? strings.pigLatinLabel
              : strings.englishBestEffort}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="pig-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Rules */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.rulesHeading}
        </h2>
        <ul className="space-y-3">
          {RULE_IDS.map((id) => (
            <li
              className="rounded-lg border border-border bg-card p-4"
              key={id}
            >
              <p className="font-medium text-foreground text-sm">
                {strings.ruleLabels[id]}
              </p>
              <p className="mt-1 font-mono text-muted-foreground text-xs">
                {strings.ruleExamples[id]}
              </p>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
